import { FormProvider } from "react-hook-form";
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/view/components/ui/dialog";
import { Button } from "@/view/components/ui/button";
import { Skeleton } from "@/view/components/ui/skeleton";
import useOffersModal from "./useOffersModal";
import { ChevronLeft, CheckCheck, Loader2 } from "lucide-react";
import { Slider } from "@/view/components/ui/slider";
import { BasicInput } from "@/view/components/BasicInput";
import { cn } from "@/app/utils/cn";
import masks, { applyMask } from "@/app/utils/masks";

interface OffersModalProps {
  onContinue: () => void;
  onBack: () => void;
}

export default function OffersModal({ onContinue, onBack }: OffersModalProps) {
  const {
    form,
    offersData,
    isLoadingOffersData,
    selectedOfferIndex,
    // setSelectedOfferIndex,
    selectedValues,
    // setSelectedValues,
    installmentsByOfferIndex,
    // handleSelectOffer,
    loadingInstallments,
    setSelectedInstallments,
    isProposalReady,
    // commissionByOfferIndex,
    // setCommissionByOfferIndex,
    handleSelectPrazo,
    errorMessage,
    submitModal,
    handleSliderChange,
    selectedInstallments,
    customerData,
    isSendingData,
    inputDigits,
    setInputDigits,
  } = useOffersModal({
    onContinue,
  });

  const {
    formState: { errors },
  } = form;

  const hasOffers = offersData?.offers && offersData.offers.length > 0;

  const allowedValues =
    offersData?.offers?.[selectedOfferIndex]?.valores?.map((v) => v.valor) ??
    [];
  const commissionValue = offersData?.offers?.[selectedOfferIndex]?.commission;

  return (
    <DialogContent className="flex max-h-[90vh] flex-col overflow-y-auto sm:max-w-3xl">
      <DialogHeader>
        <div className="gap-22 flex items-center">
          {isLoadingOffersData ? (
            <Skeleton className="mt-6 h-[50px] w-full" />
          ) : errorMessage ? (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={onBack}
                className="-ml-2 h-8 w-8 text-black01"
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
              <DialogTitle className="text-xl font-semibold text-black01">
                Opções de crédito
              </DialogTitle>
            </>
          ) : (
            <DialogTitle className="text-xl font-semibold text-black01">
              Opções de crédito
            </DialogTitle>
          )}
        </div>
      </DialogHeader>
      <form onSubmit={submitModal} className="flex flex-1 flex-col">
        <FormProvider {...form}>
          <div className="flex-1 overflow-y-auto px-1">
            {isLoadingOffersData ? (
              <Skeleton className="h-[520px] w-full" />
            ) : hasOffers ? (
              <>
                <div className="mb-6 rounded-lg bg-[#FCFCFC] p-4">
                  <div className="mb-6 border-b pb-4">
                    <h3 className="text-sm font-medium text-black01">
                      {offersData?.offers?.[selectedOfferIndex]?.nome}
                    </h3>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-medium text-gray-500">
                      <p>
                        Convênio:{" "}
                        {offersData?.offers?.[selectedOfferIndex]?.convenio}
                      </p>
                      <span className="hidden h-1 w-1 rounded-full bg-gray-300 sm:block" />
                      <p>
                        Valor liberado:{" "}
                        <span className="font-semibold text-[#528866]">
                          {masks.currency(
                            (offersData?.offers?.[selectedOfferIndex]
                              ?.maxValor ?? 0) * 100,
                          )}
                        </span>
                      </p>
                      <span className="hidden h-1 w-1 rounded-full bg-gray-300 text-sm font-medium sm:block" />
                      <p>
                        Vencimento:{" "}
                        {offersData?.offers?.[selectedOfferIndex]?.vencimento &&
                          new Date(
                            offersData.offers[selectedOfferIndex].vencimento,
                          ).toLocaleDateString("pt-BR")}
                      </p>
                    </div>
                  </div>
                  <div className="rounded-xl border border-primary p-2 px-3 shadow-sm">
                    <label className="text-xs font-semibold text-[#979797]">
                      Selecione o valor que deseja sacar
                    </label>
                    <div className="mt-2 flex items-center">
                      <span className="text-2xl font-bold text-black01">
                        R$
                      </span>
                      <input
                        type="text"
                        className="ml-2 w-full border-none bg-transparent text-xl font-semibold text-black01 focus:ring-0"
                        value={
                          inputDigits ? masks.currency(Number(inputDigits)) : ""
                        }
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, "");
                          setInputDigits(digits);
                        }}
                        onBlur={() => {
                          const value = Number(inputDigits) / 100;
                          const allowedValues =
                            offersData?.offers?.[
                              selectedOfferIndex
                            ]?.valores?.map((v) => v.valor) ?? [];
                          const closestValue = allowedValues.reduce(
                            (prev, curr) =>
                              Math.abs(curr - value) < Math.abs(prev - value)
                                ? curr
                                : prev,
                            allowedValues[0] ?? 0,
                          );

                          handleSliderChange(closestValue);
                          setInputDigits((closestValue * 100).toString());
                        }}
                      />
                    </div>
                  </div>
                  <div className="mt-6">
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex flex-col items-start">
                        <span className="text-xs font-semibold text-gray-700">
                          {masks.currency(Math.min(...allowedValues) * 100)}
                        </span>

                        <span className="text-[10px] font-bold text-gray-400">
                          {masks.currency(
                            (
                              (Math.min(...allowedValues) *
                                (commissionValue ??
                                  offersData?.offers?.[selectedOfferIndex]
                                    ?.commission)) /
                              100
                            ).toFixed(2),
                          )}
                        </span>
                      </div>
                      <div className="flex-1 px-4">
                        <Slider
                          min={Math.min(...allowedValues)}
                          max={Math.max(...allowedValues)}
                          step={1}
                          value={selectedValues[selectedOfferIndex] ?? 0}
                          allowedValues={allowedValues}
                          onChange={(e) =>
                            handleSliderChange(Number(e.target.value))
                          }
                        />
                      </div>
                      <div className="flex flex-col items-end">
                        <span className="text-xs font-semibold text-gray-700">
                          {masks.currency(Math.max(...allowedValues) * 100)}
                        </span>
                        <span className="text-[10px] font-bold text-gray-400">
                          {masks.currency(
                            (
                              (Math.max(...allowedValues) *
                                (commissionValue ??
                                  offersData?.offers?.[selectedOfferIndex]
                                    ?.commission)) /
                              100
                            ).toFixed(2),
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-8">
                    {loadingInstallments?.[selectedOfferIndex] ? (
                      <Skeleton className="h-24 w-full rounded-md" />
                    ) : (
                      <div className="flex gap-4 overflow-x-auto pb-4">
                        {(
                          installmentsByOfferIndex?.[selectedOfferIndex] || []
                        ).map((item, idx) => {
                          const isSelected =
                            selectedInstallments[selectedOfferIndex]?.prazo ===
                            item.prazo;
                          return (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => {
                                setSelectedInstallments((prev) => ({
                                  ...prev,
                                  [selectedOfferIndex]: item,
                                }));
                                handleSelectPrazo(
                                  selectedOfferIndex,
                                  item.prazo,
                                );
                              }}
                              className={cn(
                                "flex min-w-[140px] items-center justify-center gap-2 rounded-lg py-2 transition-all hover:border-primary",
                                isSelected &&
                                  "border border-primary bg-primary/5",
                              )}
                            >
                              <span
                                className={cn(
                                  "text-sm font-semibold text-black01",
                                )}
                              >
                                {masks.currency(Math.round(item.valor * 100))}
                              </span>
                              <span
                                className={cn(
                                  "text-sm font-semibold",
                                  isSelected
                                    ? "text-primary/70"
                                    : "text-gray-400",
                                )}
                              >
                                {item.prazo}x
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="mt-6">
                    <div className="flex items-center justify-between rounded-lg bg-red-50 px-4 py-3">
                      <p className="text-[10px] font-semibold text-black01">
                        Comissão (
                        {offersData?.offers?.[selectedOfferIndex]?.commission}%)
                      </p>
                      <p className="text-base font-medium text-black01">
                        {masks.currency(
                          (
                            (selectedValues[selectedOfferIndex] *
                              (commissionValue ??
                                offersData?.offers?.[selectedOfferIndex]
                                  ?.commission)) /
                            100
                          ).toFixed(2),
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-between rounded-lg bg-[#E9F4F0] p-4">
                  <div className="flex items-start gap-3">
                    <p className="text-sm font-medium italic text-[#467262]">
                      As informações abaixo estão disponíveis na sua fatura de
                      energia
                    </p>
                  </div>
                  <CheckCheck className="mt-0.5 h-5 w-5 text-[#467362]" />
                </div>

                <div className="mt-6">
                  <h4 className="mb-4 text-sm font-semibold text-black01">
                    Dados da sua conta de energia
                  </h4>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {offersData?.offers?.[selectedOfferIndex]?.adicionais?.map(
                      (adicional) => (
                        <BasicInput
                          key={adicional.convenioDadosId}
                          name={`adicionais.${selectedOfferIndex}.${adicional.convenioDadosId}`}
                          label={adicional.nome}
                          placeholder={adicional.nome}
                          maxLength={
                            adicional.nome === "Data de Leitura"
                              ? 10
                              : undefined
                          }
                          onInput={
                            adicional.nome === "Data de Leitura"
                              ? applyMask(masks.date)
                              : undefined
                          }
                          error={
                            (errors.adicionais as any)?.[selectedOfferIndex]?.[
                              adicional.convenioDadosId
                            ]?.message as string
                          }
                        />
                      ),
                    )}
                  </div>
                </div>

                <div className="mt-6">
                  <h4 className="mb-4 text-sm font-semibold text-black01">
                    Termine o cadastro dos seus dados
                  </h4>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <BasicInput
                      name="phone"
                      label="Telefone"
                      placeholder="(00) 00000-0000"
                      maxLength={15}
                      onInput={applyMask(masks.phone)}
                      defaultValue={customerData?.phone}
                      error={errors.phone?.message as string}
                    />
                    <BasicInput
                      name="email"
                      label="E-mail"
                      placeholder="seuemail@exemplo.com"
                      defaultValue={customerData?.email}
                      error={errors.email?.message as string}
                    />
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center text-center">
                <h2 className="text-xl font-semibold">
                  Nenhuma oferta disponível
                </h2>
                <p className="mt-2 max-w-md font-medium text-black04">
                  {errorMessage ||
                    "Nenhuma oferta encontrada para esta proposta."}
                </p>
              </div>
            )}
          </div>

          <div className="mt-4 flex justify-end gap-2">
            {hasOffers && (
              <Button
                type="submit"
                className="w-[160px] rounded-full bg-primary font-semibold text-white hover:bg-primary/90"
                disabled={!isProposalReady(selectedOfferIndex) || isSendingData}
              >
                {isSendingData && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Avançar
              </Button>
            )}
          </div>
        </FormProvider>
      </form>
    </DialogContent>
  );
}
